import { ArrowUp, Loader2, Sparkles } from "lucide-react";

import { AssistantDisclosureNotice } from "src/components/AssistantChat/AssistantDisclosureNotice";
import { Button } from "src/components/ui/button";
import { Textarea } from "src/components/ui/textarea";
import { formatDisplayCredits } from "src/lib/llmCredits";
import { cn } from "src/lib/utils";

const DEFAULT_PROGRESS_MESSAGE = "Working...";

const formatTargetLabel = (target, currentStoreId) => {
  const threadTitle = target?.threadTitle || "another chat";

  if (target?.integrationId && target.integrationId !== currentStoreId) {
    return `${target.storeName || "another store"} / "${threadTitle}"`;
  }

  return `"${threadTitle}"`;
};

export const AssistantComposer = ({
  isMobile,
  isCreditLocked,
  weeklyTokenLimit,
  resetDaysLabel,
  hasAnotherActiveRun,
  currentStoreId,
  activeRunTarget,
  activeRunProgressMessage,
  readyTarget,
  input,
  isComposerMultiline,
  isComposerDisabled,
  isSendDisabled,
  isStartingRun,
  composerInputRef,
  onContact,
  onOpenTarget,
  onComposerChange,
  onComposerKeyDown,
  onSend,
}) => (
  <div
    className={cn(
      isMobile
        ? "px-3 pt-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]"
        : "p-4"
    )}
  >
    <div className="mx-auto w-full max-w-3xl space-y-2">
      {isCreditLocked ? (
        <div className="bg-destructive/10 border-destructive/35 rounded-2xl border px-4 py-3 text-xs">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-foreground font-medium">
                AI credits exhausted.
              </div>
              <div className="text-muted-foreground mt-1">
                Your {formatDisplayCredits(weeklyTokenLimit)}-credit weekly
                allowance resets in {resetDaysLabel}.
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={onContact}
            >
              Contact us
            </Button>
          </div>
        </div>
      ) : null}

      {hasAnotherActiveRun ? (
        <div className="bg-card/80 border-border/80 flex items-center justify-between gap-3 rounded-2xl border px-3 py-2 text-xs">
          <div className="flex min-w-0 items-center gap-2">
            <Loader2 className="text-primary size-3.5 shrink-0 animate-spin" />
            <div className="min-w-0">
              <div className="text-foreground truncate font-medium">
                Working in {formatTargetLabel(activeRunTarget, currentStoreId)}
              </div>
              <div className="text-muted-foreground truncate">
                {activeRunProgressMessage ||
                  activeRunTarget?.progressMessage ||
                  DEFAULT_PROGRESS_MESSAGE}
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 px-2"
            onClick={() => onOpenTarget(activeRunTarget)}
          >
            Open
          </Button>
        </div>
      ) : null}

      {!hasAnotherActiveRun && readyTarget ? (
        <div className="bg-primary/10 border-primary/25 flex items-center justify-between gap-3 rounded-2xl border px-3 py-2 text-xs">
          <div className="flex min-w-0 items-center gap-2">
            <Sparkles className="text-primary size-3.5 shrink-0 animate-pulse" />
            <div className="text-foreground min-w-0 truncate font-medium">
              Reply ready in {formatTargetLabel(readyTarget, currentStoreId)}
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 px-2"
            onClick={() => onOpenTarget(readyTarget)}
          >
            Open
          </Button>
        </div>
      ) : null}

      {!isCreditLocked ? (
        <div className="bg-muted/40 focus-within:border-primary/45 rounded-[1.75rem] border transition-colors">
          <div className="relative">
            <Textarea
              ref={composerInputRef}
              className={cn(
                "max-h-44 min-h-12 resize-none border-0 bg-transparent px-4 py-3 pr-16 leading-6 shadow-none focus-visible:border-0 focus-visible:ring-0",
                isComposerMultiline && "pb-14"
              )}
              value={input}
              onChange={onComposerChange}
              onKeyDown={onComposerKeyDown}
              placeholder="Ask what changed, why it happened, or where spend went..."
              disabled={isComposerDisabled}
              rows={1}
            />

            <Button
              type="button"
              size="icon"
              className={cn(
                "absolute right-2 size-9 rounded-full",
                isComposerMultiline ? "bottom-2" : "top-1/2 -translate-y-1/2"
              )}
              aria-label="Send message"
              onClick={onSend}
              disabled={isSendDisabled}
            >
              {isStartingRun ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <ArrowUp className="size-5" />
              )}
            </Button>
          </div>
        </div>
      ) : null}

      {isMobile ? (
        <AssistantDisclosureNotice className="items-center px-1 leading-none" />
      ) : null}
    </div>
  </div>
);
